// Prerendered `/` — the marketing homepage, and the site's flagship SEO surface (its term is "ai journal"). Node-prerender-safe: MUI + react-router + the shared splash modules + literals only, no store/Amplify/@gnothi/shared/config imports, and no window/localStorage/document at module scope — this module's whole graph is eagerly imported during the Node prerender pass (see series.tsx's header). No loader, ever: the page is static literals.
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';
import { SplashChrome } from './splash-chrome';
import { Band, CtaBand, SectionHeader } from './splash-sections';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { HomePricing, HomeComplianceNotice } from './home-sections';
import { useHomeShims } from './home-shims';
import SeriesDemo, {starterAuthUrl} from './series-demo';
import FlagshipProof from './series-proof';
import type { Route } from './+types/home';

const TITLE = 'Gnothi — Stories and Audio Courses Made for Your Curiosity';
const DESCRIPTION = metaDescription(
  'Create a continuing story around what you want to learn, or make a direct audio course. Read each chapter or hear its included narration.',
);
const PATH = '/';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'MultimediaApplication',
      featureList: [
        'Continuing stories with chosen learning topics',
        'Direct audio courses developed across chapters',
        'Automatic narration included with every generated Chapter',
        'Optional journal notes and selected shared context',
        'Custom Fields with light scoring and causal analysis',
      ],
    }),
  },
];

function Hero() {
  return (
    <Grid container spacing={{xs: 4, md: 6}} sx={{alignItems: 'center'}}>
      <Grid size={{xs: 12, md: 5}}>
        <Stack spacing={2} sx={{alignItems: 'flex-start'}}>
          <Typography variant="overline" component="p" sx={{color: 'splash.accentOnDark', fontWeight: 800}}>AI-written books and podcasts</Typography>
          <Typography variant="h1" component="h1" sx={{fontSize: {xs: '2.35rem', sm: '3.25rem', md: '4rem'}, lineHeight: 1.05}}>Create a story around what you want to learn.</Typography>
          <Typography sx={{fontSize: {xs: '1rem', md: '1.15rem'}, opacity: 0.86}}>Choose a world and the subjects you're curious about. Gnothi writes and narrates a continuing series with those ideas woven into the story. Prefer a direct lesson? Make an audio course.</Typography>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{width: {xs: '100%', sm: 'auto'}}}>
            <Button variant="contained" size="large" component={RouterLink} to={starterAuthUrl('cyberpunk-agents-business')}>Create your series</Button>
            <Button component="a" href="#flagship" variant="outlined" color="inherit" size="large">Hear an example</Button>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{xs: 12, md: 7}}><Card sx={{p: {xs: 2, md: 3}, borderRadius: 4}}><FlagshipProof compact /></Card></Grid>
    </Grid>
  );
}

function StartingChoices() {
  const choices = [
    ['Story with learning', 'Choose a world and subjects. The topics affect what characters notice, decide, and do.', 'cyberpunk-agents-business'],
    ['Audio course', 'Develop subjects directly across connected chapters, with narration included.', 'course-agents-business'],
    ['Just a story', 'Start with a premise and build a continuing work of fiction without learning topics.', 'cyberpunk-fiction'],
  ] as const;
  return <>
    <SectionHeader eyebrow="Three starting points" title="Choose the form, then make it yours" deck="Every option opens an editable premise and topic list before the first chapter." />
    <Grid container spacing={2}>{choices.map(([title, body, starter]) => <Grid size={{xs: 12, md: 4}} key={title}><Card variant="outlined" sx={{height: '100%', p: 3, borderRadius: 3}}><Typography variant="h5" component="h3" sx={{mb: 1}}>{title}</Typography><Typography color="text.secondary" sx={{mb: 2}}>{body}</Typography><Button component={RouterLink} to={starterAuthUrl(starter)} variant="outlined">Start here</Button></Card></Grid>)}</Grid>
  </>;
}

function SteeringAndFormats() {
  return <>
    <SectionHeader eyebrow="Chapter by chapter" title="Add a note to steer what comes next" deck="Journal context starts off for a new Series. You can connect selected tagged notes later, revise the premise or topics, or give direct feedback." />
    <Grid container spacing={2}>
      <Grid size={{xs: 12, md: 6}}><Card variant="outlined" sx={{p: 3, height: '100%', borderRadius: 3}}><Typography variant="h5" component="h3">Optional notes and shared context</Typography><Typography color="text.secondary" sx={{mt: 1}}>A project note, learning question, character detail, or selected entry shared with you can add context when the Series source settings include it. A note can influence a later chapter; it doesn't promise that every detail will appear.</Typography></Card></Grid>
      <Grid size={{xs: 12, md: 6}}><Card variant="outlined" sx={{p: 3, height: '100%', borderRadius: 3}}><Typography variant="h5" component="h3">Read, listen, and publish</Typography><Typography color="text.secondary" sx={{mt: 1}}>Read in the chapter view, hear the included narration, export completed chapters together as an EPUB, or enable an RSS feed after choosing a public tier and confirming your rights. <RouterLink to="/publishing">See the publishing guide.</RouterLink></Typography></Card></Grid>
    </Grid>
  </>;
}

export default function Home() {
  useHomeShims();
  return (
    <SplashChrome active="home">
      <Band color="dark" hero><Hero /></Band>
      <Band id="interconnect" color="grey"><SectionHeader eyebrow="Try the idea" title="The same subjects, shaped three ways" deck="Switch between a cyberpunk story, a fantasy analogy, and a direct audio course. These examples were written for this page, so switching them is instant and uses no AI credits." /><SeriesDemo compact /></Band>
      <Band id="pillars" color="light"><StartingChoices /></Band>
      <Band id="method" color="grey"><SteeringAndFormats /></Band>
      <Band id="who" color="light"><SectionHeader eyebrow="Supporting tools" title="Keep the rest of Gnothi within reach" deck="Journal, Fields, chat, sharing, and Groups have their own homes. Use them on their own or connect the context you choose to a Series." /><Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{justifyContent: 'center'}}><Button component={RouterLink} to="/fields" variant="outlined">Explore Fields</Button><Button component={RouterLink} to="/community" variant="outlined">Explore Community</Button><Button component={RouterLink} to="/series" variant="contained">Explore Series</Button></Stack></Band>
      <Band id="pricing" color="grey"><HomePricing /></Band>
      <CtaBand
        color="light"
        title="Create your series"
        deck="Choose a starting point, edit the premise and topics, then decide when to make the first chapter."
      />
      <HomeComplianceNotice />
    </SplashChrome>
  );
}
